/** A static config for use by the whole application. */
export class Config {
	/**
	 * Loads the config.
	 * @returns {Promise<void>}
	 */
	static async load() {
		if (window.config === undefined) {
			try {
				// eslint-disable-next-line require-atomic-updates
				window.config = await (await fetch('config.json')).json();
			} catch (e) {
				console.warn('Could not find a configuration "config.json" file. You can copy the config.example.json over and use it.');
				console.warn(e.message);
			}
		}

		this.staticAssetsUrl = this._getConfigUrl('staticAssetsUrl', true);
		this.dynamicAssetsUrl = this._getConfigUrl('dynamicAssetsUrl', true);
		this.baseClimateUrl = this._getConfigUrl('baseClimateUrl', true);
		this.baseScienceUrl = this._getConfigUrl('baseScienceUrl', true);
		this.globeLat = this._getConfigNumber('globeLat', false) ?? this.globeLat;
		this.globeLong = this._getConfigNumber('globeLong', false) ?? this.globeLong;
		this.kiosk = this._getConfigBoolean('kiosk', false) ?? this.kiosk;
		this.sessionMaxTime = this._getConfigNumber('sessionMaxTime', false) ?? this.sessionMaxTime;
		this.maxInactivityTIme = this._getConfigNumber('maxInactivityTime', false) ?? this.maxInactivityTIme;

		this.spout = this._getConfigBoolean('spout', false) ?? this.spout;
		if (this.spout) {
			this.spoutRenderWidth = this._getConfigNumber('spoutRenderWidth', false) ?? this.spoutRenderWidth;
			this.spoutGlobe = this._getConfigBoolean('spoutGlobe', false) ?? this.spoutGlobe;
			this.spoutFontSize = this._getConfigNumber('spoutFontSize', false) ?? this.spoutFontSize;
			this.spoutLonAngleOffset = this._getConfigNumber('spoutLonAngleOffset', false) ?? this.spoutLonAngleOffset;
			this.spoutAlignToNorthPole = this._getConfigBoolean('spoutAlignToNorthPole', false) ?? this.spoutAlignToNorthPole;
		}
	}

	/**
	 * Gets the config value given the key.
	 * @param {string} key
	 * @param {boolean} required
	 * @returns {string}
	 * @private
	 */
	static _getConfigUrl(key, required) {
		// Get the value.
		const value = window.config[key];

		// If the variable isn't found, throw an error.
		if (required && value === undefined) {
			throw new Error(`Cannot find a window.config value for "${key}". Please be sure to enter one in the config.json file. Check config.example.json for an example.`);
		}
		if (value !== undefined && typeof value !== 'string') {
			throw new Error(`The window.config value for "${key}" must be a string. Check config.example.json for an example.`);
		}

		// Remove any trailing '/' on the end so we don't get any double slashes throughout the app.
		return value.endsWith('/') ? value.slice(0, -1) : value;
	}

	/**
	 * Gets the config value given the key.
	 * @param {string} key
	 * @param {boolean} required
	 * @returns {boolean}
	 * @private
	 */
	static _getConfigBoolean(key, required) {
		// Get the value.
		const value = window.config[key];

		// If the variable isn't found, throw an error.
		if (required && value === undefined) {
			throw new Error(`Cannot find a window.config value for "${key}". Please be sure to enter one in the config.json file. Check config.example.json for an example.`);
		}
		if (value !== undefined && typeof value !== 'boolean') {
			throw new Error(`The window.config value for "${key}" must be a boolean. Check config.example.json for an example.`);
		}

		return value;
	}

	/**
	 * Gets the config value given the key.
	 * @param {string} key
	 * @param {boolean} required
	 * @returns {number}
	 * @private
	 */
	static _getConfigNumber(key, required) {
		// Get the value.
		const value = window.config[key];

		// If the variable isn't found, throw an error.
		if (required && value === undefined) {
			throw new Error(`Cannot find a window.config value for "${key}". Please be sure to enter one in the config.json file. Check config.example.json for an example.`);
		}
		if (value !== undefined && typeof value !== 'number') {
			throw new Error(`The window.config value for "${key}" must be a number. Check config.example.json for an example.`);
		}

		return value;
	}

	/**
	 * The URL to Pioneer Assets
	 * @type {string}
	 */
	static staticAssetsUrl = '';

	/**
	 * The URL to the dynamic assets.
	 * @type {string}
	 */
	static dynamicAssetsUrl = '';

	/**
	 * The URL to the Climate site.
	 * @type {string}
	 */
	static baseClimateUrl = '';

	/**
	 * The URL to the Science site.
	 * @type {string}
	 */
	static baseScienceUrl = '';

	/**
	 * The latitide and longitude to focus on for globe orientation
	 * @type {number}
	 */
	static globeLat = 38;

	static globeLong = 30;

	/**
	 * Whether or not we're running in kiosk mode.
	 * @type {boolean}
	 */
	static kiosk = false;

	/**
	 * How long in milliseconds before the session stops.
	 * Default is 5 min
	 * @type {number}
	 */
	static sessionMaxTime = 300000;

	/**
	 * How long in milliseconds without interaction before autoplay starts
	 * Default is 6 min - must be greater than sessionMaxTime
	 * @type {number}
	 */
	static maxInactivityTIme = 360000;

	/*
	 * Whether or not Spout is enabled.
	 * @type {boolean}
	 */
	static spout = false;

	/**
	 * The width of the Spout renderer.
	 * @type {number}
	 */
	static spoutRenderWidth = 2048;

	/**
	 * Whether or not we're projecting to a globe.
	 * @type {boolean}
	 */
	static spoutGlobe = true;

	/**
	 * The font size of the labels on the globe.
	 * @type {number}
	 */
	static spoutFontSize = 24;

	/**
	 * The longitude angle offset, so it can be turned toward the viewer if needed.
	 * @type {number}
	 */
	static spoutLonAngleOffset = 0;

	/**
	 * Whether or not to align it to the north pole.
	 * @type {boolean}
	 */
	static spoutAlignToNorthPole = false;
}
